import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { moduleForPath } from "@/components/layout/navConfig";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { fmtDate } from "@/lib/format";
import WorkspaceDashboard from "@/components/workspace/WorkspaceDashboard";

/* Document lists the app already loads on its own pages. Nothing here is a new endpoint. */
const SOURCES = [
  { key: "indent", label: "Purchase Indent", api: "/procurement/indents", route: "/erp/operations/purchase-indents" },
  { key: "pr", label: "Purchase Requisition", api: "/procurement/requisitions", route: "/erp/operations/purchase-requisitions" },
  { key: "po", label: "Purchase Order", api: "/procurement/purchase-orders", route: "/erp/operations/purchase-orders" },
  { key: "wo", label: "Work Order", api: "/procurement/work-orders", route: "/erp/operations/work-orders" },
  { key: "grn", label: "Goods Receipt Note", api: "/procurement/grns", route: "/erp/operations/goods-receipt-notes" },
  { key: "pb", label: "Purchase Bill", api: "/procurement/purchase-bills", route: "/erp/operations/purchase-bills" },
  { key: "sb", label: "Service Bill", api: "/procurement/service-bills", route: "/erp/operations/service-bills" },
  { key: "ra", label: "RA Bill", api: "/revenue/ra-bills", route: "/erp/delivery/ra-bills" },
];
const PENDING = ["SUBMITTED", "PENDING_APPROVAL"];
const NUMBER_KEYS = ["number", "po_number", "bill_number", "doc_number"];
const DATE_KEYS = ["po_date", "requisition_date", "indent_date", "bill_date", "order_date", "grn_date", "receipt_date", "date", "created_at"];
const AMOUNT_KEYS = ["total_amount", "net_payable", "net_receivable", "amount", "total"];

const pick = (row, keys) => { for (const k of keys) if (row[k] !== undefined && row[k] !== null && row[k] !== "") return row[k]; return null; };
const money = (v) => (v === null ? "" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);

const QUICK = [
  { label: "Purchase Orders", to: "/erp/operations/purchase-orders" },
  { label: "Goods Receipt Notes", to: "/erp/operations/goods-receipt-notes" },
  { label: "Purchase Bills", to: "/erp/operations/purchase-bills" },
  { label: "Vendor Payments", to: "/erp/operations/vendor-payments" },
  { label: "RA Bills", to: "/erp/delivery/ra-bills" },
  { label: "Client Payments", to: "/erp/delivery/client-payments" },
  { label: "All Projects", to: "/erp/delivery/all-projects" },
  { label: "Stock & Balances", to: "/erp/operations/stock-and-balances" },
];

function Panel({ title, count, children, testId }) {
  return (
    <section className="rounded-lg border border-border bg-card" data-testid={testId}>
      <header className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {count !== undefined && <span className="rounded bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">{count}</span>}
      </header>
      {children}
    </section>
  );
}

const Empty = ({ text }) => <p className="px-4 py-8 text-center text-sm text-muted-foreground">{text}</p>;

export default function MyWorkspace() {
  const { user } = useAuth();
  const { canView, loading: permLoading } = usePermissions();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const allowed = useMemo(
    () => SOURCES.filter((s) => canView(moduleForPath(s.route))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permLoading]
  );

  useEffect(() => {
    if (permLoading) return undefined;
    let live = true;
    setLoading(true);
    Promise.allSettled([
      ...allowed.map((s) => api.get(s.api)),
    ]).then((res) => {
      if (!live) return;
      const rows = [];
      allowed.forEach((s, i) => {
        const r = res[i];
        if (r.status !== "fulfilled" || !Array.isArray(r.value.data)) return;
        r.value.data.forEach((d) => {
          const date = pick(d, DATE_KEYS);
          rows.push({
            id: `${s.key}-${d.id}`, source: s, status: d.status,
            number: pick(d, NUMBER_KEYS) || String(d.id).slice(0, 8),
            date, sort: dayjs(d.created_at || date).valueOf() || 0, amount: pick(d, AMOUNT_KEYS),
          });
        });
      });
      setDocs(rows);
      setLoading(false);
    });
    return () => { live = false; };
  }, [allowed, permLoading]);

  const awaiting = docs.filter((d) => PENDING.includes(d.status)).sort((a, b) => b.sort - a.sort);
  const drafts = SOURCES.map((s) => ({ s, n: docs.filter((d) => d.source.key === s.key && d.status === "DRAFT").length })).filter((x) => x.n > 0);
  const draftTotal = drafts.reduce((a, x) => a + x.n, 0);
  const recent = [...docs].sort((a, b) => b.sort - a.sort).slice(0, 8);
  const quick = QUICK.filter((q) => canView(moduleForPath(q.to)));
  const first = (user?.full_name || "").split(" ")[0];

  return (
    <WorkspaceDashboard user={user} loading={loading} awaiting={awaiting} drafts={drafts} draftTotal={draftTotal} recent={recent} quick={quick} />
  );
}
